import { Box, TextField, Typography, Stack, FormControl, InputLabel, Select, MenuItem, FormGroup, FormControlLabel, Switch, Divider, Tooltip, Alert, IconButton } from "@mui/material";
import MediaPicker from "../MediaPicker";
import { TikTokConfig } from "../models";
import { Info, AutoAwesome } from "@mui/icons-material";

type Props = {
  caption: string;
  onCaptionChange: (text: string) => void;
  media: string[];
  onMediaChange: (urls: string[]) => void;
  onMediaKindsChange?: (info: { hasPhoto: boolean; hasVideo: boolean }) => void;
  config: TikTokConfig;
  onConfigChange: (cfg: TikTokConfig) => void;
  maxLength: number;
  showCaptionWarning?: boolean;
  onAiClick?: () => void;
};

export default function TikTokTab({
  caption,
  onCaptionChange,
  media,
  onMediaChange,
  onMediaKindsChange,
  config,
  onConfigChange,
  maxLength,
  onAiClick
}: Props) {
  const over = caption.length > maxLength;
  const updateCfg = (patch: Partial<TikTokConfig>) => onConfigChange({ ...config, ...patch });

  return (
    <Box sx={styles.root}>
      <Box sx={styles.box}>
        <TextField
          multiline
          minRows={5}
          label="TikTok caption"
          value={caption}
          onChange={(e) => onCaptionChange(e.target.value)}
          fullWidth
          sx={onAiClick ? styles.aiTextField : undefined}
        />
        {onAiClick && (
          <Box sx={styles.aiButton}>
            <Tooltip title="Generate caption with AI">
              <IconButton size="small" color="secondary" onClick={onAiClick}>
                <AutoAwesome fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <Box sx={styles.counter}>
          <Typography variant="caption" color={over ? "error.main" : "text.secondary"}>
            {caption.length} / {maxLength}
          </Typography>
        </Box>
      </Box>

      {media.length === 0 && (
        <Alert variant="filled" severity="error">At least one media is required to post on TikTok.</Alert>
      )}

      <Box sx={styles.mediaHeaderBox}>
        <Typography sx={styles.sectionTitle} variant="subtitle2">Add Media</Typography>
        <Tooltip title="You can upload up to 10 media, 2 GB each" color="text.secondary">
          <Info fontSize="small" color="action" />
        </Tooltip>
      </Box>

      <MediaPicker value={media} onChange={onMediaChange} onKindsChange={onMediaKindsChange} />

      <Divider />

      <Typography variant="subtitle2" sx={styles.sectionTitle}>Platform Configurations</Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          label="Title (optional)"
          value={config.title ?? ""}
          onChange={(e) => updateCfg({ title: e.target.value })}
          fullWidth
        />
        <FormControl size="small" sx={styles.privacyControl}>
          <InputLabel id="tiktok-privacy-label">Privacy</InputLabel>
          <Select
            labelId="tiktok-privacy-label"
            label="Privacy"
            value={config.privacyStatus ?? "public"}
            onChange={(e) => updateCfg({ privacyStatus: e.target.value as any })}
          >
            <MenuItem value="public">Public</MenuItem>
            <MenuItem value="private">Private</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <FormGroup row sx={styles.formGroup}>
        <FormControlLabel
          control={<Switch checked={!!config.allowComment} onChange={(_, v) => updateCfg({ allowComment: v })} />}
          label="Allow comments"
        />
        <FormControlLabel
          control={<Switch checked={!!config.allowDuet} onChange={(_, v) => updateCfg({ allowDuet: v })} />}
          label="Allow duet"
        />
        <FormControlLabel
          control={<Switch checked={!!config.allowStitch} onChange={(_, v) => updateCfg({ allowStitch: v })} />}
          label="Allow stitch"
        />
        <Tooltip title="Will automatically add music to photo posts">
          <FormControlLabel
            control={<Switch checked={!!config.autoAddMusic} onChange={(_, v) => updateCfg({ autoAddMusic: v })} />}
            label="Auto add music"
          />
        </Tooltip>
        <Tooltip title="Will create a draft upload to TikTok, posting will need to be completed from within the app">
          <FormControlLabel
            control={<Switch checked={!!config.isDraft} onChange={(_, v) => updateCfg({ isDraft: v })} />}
            label="Create as draft"
          />
        </Tooltip>
        <Tooltip title="Disclose your brand on TikTok">
          <FormControlLabel
            control={
              <Switch
                checked={!!config.discloseYourBrand}
                onChange={(_, v) => updateCfg({ discloseYourBrand: v })}
              />
            }
            label="Disclose your brand"
          />
        </Tooltip>
        <Tooltip title="Disclose branded content on TikTok">
          <FormControlLabel
            control={
              <Switch
                checked={!!config.discloseBrandedContent}
                onChange={(_, v) => updateCfg({ discloseBrandedContent: v })}
              />
            }
            label="Disclose branded content"
          />
        </Tooltip>
        <Tooltip title="Flag content as AI generated on TikTok">
          <FormControlLabel
            control={<Switch checked={!!config.isAiGenerated} onChange={(_, v) => updateCfg({ isAiGenerated: v })} />}
            label="AI generated"
          />
        </Tooltip>
      </FormGroup>
    </Box>
  );
}

const styles = {
  root: { 
    display: "flex", 
    flexDirection: "column", 
    gap: 2 
  },
  box: { 
    position: "relative" 
  },
  aiTextField: { 
    '& .MuiInputBase-input': { pb: '32px' } 
  },
  aiButton: { 
    position: "absolute", bottom: 6, left: 8 
  },
  counter: { 
    position: "absolute", bottom: 6, right: 8 
  },
  mediaHeaderBox: { 
    display: "flex", alignItems: "center", fontSize: 16, gap: 1 
  },
  sectionTitle: { 
    fontSize: 16, fontWeight: 'bold' 
  },
  helperTextBox: { 
    display: "flex", justifyContent: "space-between" 
  },
  privacyControl: { 
    minWidth: 180 
  },
  formGroup: { 
    columnGap: 3 
  }
};
